import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DefaultDialogConfig } from '../../../shared/classes/default-dialog-config';

interface MessageButton {
  /** The button text */
  label: string;
  /** The signal to return from the dialog if this button is pressed */
  output: any;
}

type MessageText = string | string[];

/** The theme of the dialog. This informs iconography as well as dialog color. */
export enum MessageDialogTheme {
  /** No specific iconography or coloring. */
  Neutral = 'neutral',
  /** The dialog is alerting the user to something concerning. */
  Alert = 'alert',
  /** The dialog is informing the user of a successful action. */
  Success = 'success',
  /** The dialog is confirming a dangerous action, such as deletion. */
  Danger = 'danger',
}

export interface MessageDialogData {
  theme: MessageDialogTheme;
  /** The dialog title. (Optional.) */
  title?: string;
  /** The dialog message. If given an array, each array element will be its own paragraph. */
  message: MessageText;
  /** The primary action, such as acknowledgement or confirmation. */
  primaryButton: MessageButton;
  /** The secondary action, such as cancellation. This is optional. */
  secondaryButton?: MessageButton;
}

export class MessageDialogConfig extends DefaultDialogConfig<MessageDialogData> {
  public disableClose = true;
  public width = '420px';

  constructor(data: MessageDialogData) {
    super({ data });
  }
}

export class AlertMessageDialogConfig extends MessageDialogConfig {
  constructor(message: MessageText, buttonLabel = 'OK') {
    super({
      theme: MessageDialogTheme.Alert,
      message,
      primaryButton: { output: null, label: buttonLabel },
    });
  }
}

export class SuccessMessageDialogConfig extends MessageDialogConfig {
  constructor(message: MessageText, buttonLabel = 'OK') {
    super({
      theme: MessageDialogTheme.Success,
      message,
      primaryButton: { output: null, label: buttonLabel },
    });
  }
}

export class ConfirmMessageDialogConfig extends MessageDialogConfig {
  constructor(
    config: {
      /** The dialog theme, which affects display features like the icon. Defaults to `MessageDialogTheme.Neutral` */
      theme?: MessageDialogTheme,
      /** The dialog title. If not provided, the dialog will not have a title at all. */
      title?: string,
      /** The text of the dialog. If an array, each array element will be a paragraph in the dialog. */
      message: MessageText,
      /** The text of the secondary button, which will resolve to `false`. */
      secondary: string,
      /** The text of the primary button, which will resolve to `true`. */
      primary: string
    }
  ) {
    super({
      theme: config.theme ?? MessageDialogTheme.Neutral,
      title: config.title,
      message: config.message,
      secondaryButton: { output: false, label: config.secondary },
      primaryButton: { output: true, label: config.primary },
    });
  }
}

export class DangerConfirmationDialogConfig extends ConfirmMessageDialogConfig {
  constructor(message: MessageText, button: string, title?: string) {
    super({
      theme: MessageDialogTheme.Danger,
      title,
      message,
      secondary: 'Cancel',
      primary: button,
    });
  }
}

@Component({
  selector: 'app-message-dialog',
  templateUrl: './message-dialog.component.html',
  styleUrls: ['./message-dialog.component.scss'],
  standalone: false
})
export class MessageDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<MessageDialogComponent>>(MatDialogRef);
  readonly data = inject<MessageDialogData>(MAT_DIALOG_DATA);

  public readonly MessageDialogTheme = MessageDialogTheme;

  public theme = this.data.theme;
  public title = this.data.title;
  public message = [this.data.message].flat();
  public primaryButton = this.data.primaryButton;
  public secondaryButton = this.data.secondaryButton;

  public pick(button: MessageButton) {
    this.dialogRef.close(button.output);
  }
}
